import { useState } from 'react'
import { FileText, Loader2, PencilIcon, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type RiskReductionMeasure,
  useAttachRiskReductionMeasureImplementationProofSource,
  useDetachRiskReductionMeasureImplementationProofSource,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { ProductSource } from '@/shared/hooks/api/productSource'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { EditRiskReductionMeasureImplementationProofDialog } from './EditRiskReductionMeasureImplementationProofDialog'
import { EditRiskReductionMeasureImplementationStatusDialog } from './EditRiskReductionMeasureImplementationStatusDialog'
import { EditRiskReductionMeasureStandardConformityDialog } from './EditRiskReductionMeasureStandardConformityDialog'
import { RiskReductionMeasureProofSourceSelectDialog } from './RiskReductionMeasureProofSourceSelectDialog'
import { RrmImplementationBadge } from './RrmImplementationBadge'

type RiskReductionMeasureImplementationSectionProps = {
  complianceId: string
  productId: string | null | undefined
  riskReductionMeasure: RiskReductionMeasure
  isUpdating: boolean
}

export function RiskReductionMeasureImplementationSection({
  complianceId,
  productId,
  riskReductionMeasure,
  isUpdating,
}: RiskReductionMeasureImplementationSectionProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const attachMutation =
    useAttachRiskReductionMeasureImplementationProofSource(complianceId)
  const detachMutation =
    useDetachRiskReductionMeasureImplementationProofSource(complianceId)

  const [isProofSelectDialogOpen, setIsProofSelectDialogOpen] = useState(false)
  const [isImplementationStatusDialogOpen, setIsImplementationStatusDialogOpen] =
    useState(false)
  const [isImplementationNoteDialogOpen, setIsImplementationNoteDialogOpen] =
    useState(false)
  const [isStandardConformityDialogOpen, setIsStandardConformityDialogOpen] =
    useState(false)
  const [detachingSourceId, setDetachingSourceId] = useState<string | null>(null)

  const proofSources = riskReductionMeasure.implementationProofSources
  const hasProofSources = proofSources.length > 0
  const hasProductId = productId != null && productId.trim() !== ''
  const isProofMutationPending =
    attachMutation.isPending || detachingSourceId != null

  let implementationNote = t(
    'riskReductionMeasuresLibraryPage.implementation.cardNoteEmpty'
  )
  if (
    riskReductionMeasure.implementationNote != null &&
    riskReductionMeasure.implementationNote.trim().length > 0
  ) {
    implementationNote = riskReductionMeasure.implementationNote
  }

  const handleAttachSources = async (sources: ProductSource[]) => {
    let attachError: Error | null = null

    for (const source of sources) {
      try {
        await attachMutation.mutateAsync({
          riskReductionMeasureId: riskReductionMeasure.id,
          productSourceId: source.id,
        })
      } catch (error) {
        attachError =
          error instanceof Error
            ? error
            : new Error(t('riskReductionMeasuresLibraryPage.saveFailureUpdate'))
      }
    }

    if (attachError != null) {
      toast.error(attachError.message)
      return
    }

    setIsProofSelectDialogOpen(false)
    toast.success(
      t('riskReductionMeasuresLibraryPage.implementation.attachSuccess')
    )
  }

  const handleDetachSource = (productSourceId: string) => {
    setDetachingSourceId(productSourceId)
    detachMutation.mutate(
      {
        riskReductionMeasureId: riskReductionMeasure.id,
        productSourceId,
      },
      {
        onError: (error) => {
          toast.error(
            error.message ??
              t('riskReductionMeasuresLibraryPage.implementation.detachError')
          )
        },
        onSettled: () => {
          setDetachingSourceId(null)
        },
      }
    )
  }

  const linkedSourceIds = proofSources.map((source) => source.id)
  const isImplemented =
    riskReductionMeasure.implementationStatus === 'IMPLEMENTED'
  const conformityStandards =
    riskReductionMeasure.standardConformity?.standards ?? []
  const hasConformityStandards = conformityStandards.length > 0
  let conformityNote: string | null = null
  if (
    riskReductionMeasure.standardConformity?.note != null &&
    riskReductionMeasure.standardConformity.note.trim().length > 0
  ) {
    conformityNote = riskReductionMeasure.standardConformity.note
  }

  return (
    <section className='space-y-4'>
      <h2 className='text-lg font-semibold tracking-tight'>
        {t('riskReductionMeasuresLibraryPage.implementation.sectionTitle')}
      </h2>

      <div className='grid gap-2 sm:grid-cols-2'>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>
              {t('riskReductionMeasuresLibraryPage.implementation.statusLabel')}
            </ItemTitle>
            <ItemDescription>
              <RrmImplementationBadge
                riskReductionMeasure={riskReductionMeasure}
              />
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8 shrink-0 text-muted-foreground'
              onClick={() => setIsImplementationStatusDialogOpen(true)}
              disabled={isUpdating}
              aria-label={t(
                'riskReductionMeasuresLibraryPage.rrmDetail.editImplementationStatusAria'
              )}
            >
              <PencilIcon className='size-4' />
            </Button>
          </ItemActions>
        </Item>

        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>
              {t('riskReductionMeasuresLibraryPage.conformity.cardTitle')}
            </ItemTitle>
            <ItemDescription>
              {hasConformityStandards && (
                <div className='flex flex-wrap gap-2'>
                  {conformityStandards.map((standard) => (
                    <Badge
                      key={standard.id}
                      variant='outline'
                      className='font-normal text-muted-foreground'
                    >
                      {standard.reference}
                    </Badge>
                  ))}
                </div>
              )}
              {!hasConformityStandards &&
                t(
                  'riskReductionMeasuresLibraryPage.conformity.cardStandardsEmpty'
                )}
            </ItemDescription>
            {conformityNote && (
              <ItemDescription className='line-clamp-3 whitespace-pre-wrap'>
                {conformityNote}
              </ItemDescription>
            )}
          </ItemContent>
          <ItemActions>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-8 shrink-0 text-muted-foreground'
                    onClick={() => {
                      if (isImplemented) {
                        setIsStandardConformityDialogOpen(true)
                      }
                    }}
                    disabled={isUpdating || !isImplemented}
                    aria-label={t(
                      'riskReductionMeasuresLibraryPage.rrmDetail.editStandardConformityAria'
                    )}
                  >
                    <PencilIcon className='size-4' />
                  </Button>
                </span>
              </TooltipTrigger>
              {!isImplemented && (
                <TooltipContent>
                  {t(
                    'riskReductionMeasuresLibraryPage.conformity.availableWhenImplemented'
                  )}
                </TooltipContent>
              )}
            </Tooltip>
          </ItemActions>
        </Item>

        <Item variant='outline' className='sm:col-span-2'>
          <ItemContent>
            <ItemTitle>
              {t(
                'riskReductionMeasuresLibraryPage.implementation.noteCardTitle'
              )}
            </ItemTitle>
            <ItemDescription className='whitespace-pre-wrap line-clamp-3'>
              {implementationNote}
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8 shrink-0 text-muted-foreground'
              onClick={() => setIsImplementationNoteDialogOpen(true)}
              disabled={isUpdating}
              aria-label={t(
                'riskReductionMeasuresLibraryPage.rrmDetail.editImplementationProofAria'
              )}
            >
              <PencilIcon className='size-4' />
            </Button>
          </ItemActions>
        </Item>
      </div>

      <div className='space-y-3'>
        <div className='flex items-center justify-between gap-2'>
          <h3 className='text-sm font-medium tracking-tight'>
            {t(
              'riskReductionMeasuresLibraryPage.implementation.proofSourcesLabel'
            )}
          </h3>
          {hasProofSources && hasProductId && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setIsProofSelectDialogOpen(true)}
              disabled={isProofMutationPending}
            >
              {t(
                'riskReductionMeasuresLibraryPage.implementation.addProofSources'
              )}
            </Button>
          )}
        </div>

        {!hasProofSources && (
          <Empty className='rounded-lg border border-dashed py-8'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <FileText className='size-6' />
              </EmptyMedia>
              <EmptyTitle>
                {t(
                  'riskReductionMeasuresLibraryPage.implementation.proofSourcesEmptyTitle'
                )}
              </EmptyTitle>
              <EmptyDescription>
                {t(
                  'riskReductionMeasuresLibraryPage.implementation.proofSourcesEmptyDescription'
                )}
              </EmptyDescription>
            </EmptyHeader>
            {hasProductId && (
              <EmptyContent>
                <Button
                  size='sm'
                  onClick={() => setIsProofSelectDialogOpen(true)}
                  disabled={isProofMutationPending}
                >
                  {t(
                    'riskReductionMeasuresLibraryPage.implementation.addProofSources'
                  )}
                </Button>
              </EmptyContent>
            )}
          </Empty>
        )}

        {hasProofSources && (
          <ul className='space-y-1'>
            {proofSources.map((source) => {
              const isDetaching = detachingSourceId === source.id

              return (
                <li key={source.id}>
                  <Item variant='outline' size='sm'>
                    <ItemContent className='min-w-0 flex-1'>
                      <ItemTitle className='cursor-default text-foreground'>
                        {source.name}
                      </ItemTitle>
                      <ItemDescription className='truncate text-xs text-muted-foreground'>
                        {source.ingestionStatus}
                      </ItemDescription>
                    </ItemContent>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='shrink-0 text-muted-foreground hover:text-destructive'
                      onClick={() => handleDetachSource(source.id)}
                      disabled={isProofMutationPending}
                      aria-label={t(
                        'riskReductionMeasuresLibraryPage.implementation.unlinkProofSourceAria',
                        { name: source.name }
                      )}
                    >
                      {isDetaching ? (
                        <Loader2 className='size-4 animate-spin' />
                      ) : (
                        <>
                          <Trash2 className='size-4' />
                          {t(
                            'riskReductionMeasuresLibraryPage.implementation.removeProofSourceAction'
                          )}
                        </>
                      )}
                    </Button>
                  </Item>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {hasProductId && (
        <RiskReductionMeasureProofSourceSelectDialog
          productId={productId}
          open={isProofSelectDialogOpen}
          onOpenChange={setIsProofSelectDialogOpen}
          linkedSourceIds={linkedSourceIds}
          onConfirm={(sources) => {
            void handleAttachSources(sources)
          }}
          isSubmitting={attachMutation.isPending}
        />
      )}

      <EditRiskReductionMeasureImplementationStatusDialog
        isOpen={isImplementationStatusDialogOpen}
        onClose={() => setIsImplementationStatusDialogOpen(false)}
        complianceId={complianceId}
        riskReductionMeasure={riskReductionMeasure}
      />

      <EditRiskReductionMeasureImplementationProofDialog
        isOpen={isImplementationNoteDialogOpen}
        onClose={() => setIsImplementationNoteDialogOpen(false)}
        complianceId={complianceId}
        riskReductionMeasure={riskReductionMeasure}
      />

      <EditRiskReductionMeasureStandardConformityDialog
        isOpen={isStandardConformityDialogOpen}
        onClose={() => setIsStandardConformityDialogOpen(false)}
        complianceId={complianceId}
        riskReductionMeasure={riskReductionMeasure}
      />
    </section>
  )
}
